/**
 * @file BuilderTopBar.tsx
 * @description Верхняя панель конструктора сайта (phase19.16 Р1-6а;
 * uiux-builder-shell.md §2.1, §3.1; SC33, SC34): «Конструктор сайта», что
 * открыто (страница и адрес или экран магазина), два статуса, «Весь сайт ↗»
 * (предпросмотр на весь экран), «⋯» (История · Открыть сайт · Сохранить
 * вид как дизайн · Начать с вида по умолчанию · Отменить изменения вида ·
 * Выйти; SB138 — «Закрыть сайт на время работ…» / «Открыть сайт для
 * покупателей», `useSiteAccessMenu`) и одно главное действие. До 1024 главное действие — в нижней
 * полосе каркаса рядом с «Весь сайт ↗».
 * phase19.16 Р2: «Отменить / Вернуть» действия с блоками открытой страницы
 * (Ctrl+Z / Ctrl+Shift+Z; Р3а — отмена действует на черновик страницы).
 * QA SB120 3б: «Отменить / Вернуть» уже 1536 — значками, статусы в шапке
 * на 1024–1279 — значками с числом (`BuilderStatusPills`): название не
 * сжимается до нуля и ничего не наезжает на кнопки на 1024 и 1280.
 */

import { SITE_SLOTS, type SiteSlotKey } from "@chaiberi/shared";
import { ExternalLink, Eye, History, LogOut, Maximize2, Redo2, RotateCcw, SwatchBook, Undo2 } from "lucide-react";

import { Button } from "../../ui/button";
import { CabinetPageHeader } from "../../ui/CabinetPageHeader";
import type { PageAction } from "../../ui/page-actions";
import { useSiteView } from "../../site-view/site-view-context";
import { useBuilder } from "../builder-context";
import { BuilderStatusPills } from "./BuilderStatusPills";
import { useBuilderMainAction } from "./useBuilderMainAction";
import { useSiteAccessMenu } from "../../site-access/useSiteAccessMenu";
import { shortActionLabel } from "../../../lib/site-builder/publish-rules";
import { useCurrentContext } from "../../../auth/hooks";
import { discardableParts } from "../../../lib/site-view/changes";
import { BUILDER_SCREEN_SUBTITLES } from "../../../lib/site-builder/panels";
import { PREVIEW_SCREENS } from "../../../lib/site-view/preview";
import { useOpenSite } from "../../../hooks/site-access/useOpenSite";
import { THEME_RESET_DESCRIPTION, THEME_RESET_TITLE, themeResetKeepingShell } from "../../../lib/site-view/theme-reset";

/** «Страница «О нас» · /about»; у главной адреса «/» не пишем — висячий разделитель. */
function pageSubtitle(page: { title: string; slug: string; kind: string; slot?: SiteSlotKey | null }): string {
  // Место блога — служебная страница: только имя места, без технического адреса.
  if (page.kind === "SLOT") return SITE_SLOTS.find((s) => s.key === page.slot)?.label ?? "Блоки блога";
  const path = page.kind === "SYSTEM_HOME" || !page.slug ? "" : `/${page.slug}`;
  return path ? `Страница «${page.title}» · ${path}` : `Страница «${page.title}»`;
}

export function BuilderTopBar() {
  const view = useSiteView();
  const b = useBuilder();
  const slug = useCurrentContext()?.shop?.slug ?? "";
  const main = useBuilderMainAction();
  const siteAccessMenu = useSiteAccessMenu();
  // При закрытом магазином сайте — через пропуск сотрудника (SB138).
  const openSite = useOpenSite(view.siteUrl);
  const page = b.pages.page;
  const screen = b.selection.screen ? PREVIEW_SCREENS.find((s) => s.key === b.selection.screen) : null;
  const subtitle = screen
    ? (BUILDER_SCREEN_SUBTITLES[screen.key] ?? `Экран магазина «${screen.label}» — меняется только вид`)
    : page
      ? pageSubtitle(page)
      : "Страницы, блоки и вид сайта — в одном окне";
  const theme = view.parts?.theme;

  async function resetTheme() {
    if (!theme) return;
    const ok = await b.confirm({
      title: THEME_RESET_TITLE,
      description: THEME_RESET_DESCRIPTION,
      action: "Заменить",
      danger: true,
    });
    // Шапка, подвал и вид корзины остаются (SB120 этап 4).
    if (ok) theme.replace(themeResetKeepingShell(theme.theme));
  }

  const menu: PageAction[] = [
    { key: "history", label: "История публикаций", icon: History, run: () => void b.select({ panel: "history" }) },
    ...(openSite ? [{ key: "open", label: "Открыть сайт", icon: ExternalLink, run: () => openSite("/") }] : []),
    ...(b.canEditSettings ? [{ key: "design", label: "Сохранить вид как дизайн…", icon: SwatchBook, run: b.openSaveDesign }] : []),
    ...(view.editableParts.includes("THEME") ? [{ key: "reset", label: "Начать с вида по умолчанию…", icon: RotateCcw, run: () => void resetTheme() }] : []),
    // SB138: «Закрыть сайт на время работ…» / «Снять паузу сайта».
    ...siteAccessMenu.items,
    {
      key: "discard",
      label: "Отменить изменения вида…",
      icon: Undo2,
      danger: true,
      disabled: !view.changes || discardableParts(view.changes).length === 0,
      run: () => view.openDialog("discard"),
    },
    { key: "exit", label: "Выйти из конструктора", icon: LogOut, to: `/shop/${slug}` },
  ];

  const disabledReason = main.action.kind === "disabled" ? main.action.reason : null;
  const publish = (
    <Button variant="brand" disabled={main.action.kind === "disabled"} loading={main.busy} onClick={main.run} title={disabledReason ?? undefined}>
      {main.action.label}
    </Button>
  );

  return (
    <>
      <CabinetPageHeader
        title="Конструктор сайта"
        subtitle={subtitle}
        status={<BuilderStatusPills />}
        secondary={[
          ...(page && b.canEditPages
            ? [
                { key: "undo", label: "Отменить", iconOnlyBelow: "2xl" as const, hint: b.undo.undoLabel ? `Отменить: ${b.undo.undoLabel} (Ctrl+Z)` : "Отменять нечего", icon: Undo2, disabled: !b.undo.canUndo, loading: b.undo.busy, run: () => void b.undo.undo() },
                { key: "redo", label: "Вернуть", iconOnlyBelow: "2xl" as const, hint: b.undo.redoLabel ? `Вернуть: ${b.undo.redoLabel} (Ctrl+Shift+Z)` : "Возвращать нечего", icon: Redo2, disabled: !b.undo.canRedo, run: () => void b.undo.redo() },
              ]
            : []),
          { key: "whole", label: "Весь сайт ↗", icon: Maximize2, run: b.openWholeSite },
        ]}
        menu={menu}
        // На телефоне шапка без `mobileAction` уносит `action` в нижнюю полосу — при просмотре
        // версии её не должно быть совсем (QA Р3а D8, повторный прогон 360).
        action={b.override?.pageVersion && b.layout === "phone" ? undefined : publish}
        mobileAction={
          // Просмотр версии страницы: нижняя полоса закрыла бы полосу витрины «Версия N от …»
          // (QA Р3а D8) — действия версии в полосе над канвой.
          b.override?.pageVersion ? undefined : (
          // [Смотреть] — текстом, как в проекте; главное действие шире, на 360 — короткой подписью.
          <div className="flex gap-2">
            <Button variant="outline" className="h-12 shrink-0" onClick={b.openWholeSite}>
              <Eye aria-hidden />
              Смотреть
            </Button>
            <Button variant="brand" className="h-12 min-w-0 flex-1" disabled={main.action.kind === "disabled"} loading={main.busy} onClick={main.run}>
              <span className="truncate max-[419px]:hidden">{main.action.label}</span>
              <span className="truncate min-[420px]:hidden">{shortActionLabel(main.action.label)}</span>
            </Button>
          </div>
          )
        }
      />
      {siteAccessMenu.dialog}
    </>
  );
}
